import { Link } from "react-router-dom";
import { ArrowRight, Shield, Zap, Award, Clock } from "lucide-react";
import { useLang } from "@/context/LangContext";

const defaultFeatures = [
  {
    icon: Shield,
    value: "2-in-1",
    title: "Security + Certification Combined",
    desc: "No need for separate vendors. GACA integrates deep cybersecurity expertise into every certification engagement. Your audit isn't just a compliance exercise — it's a thorough security evaluation.",
  },
  {
    icon: Clock,
    value: "90 Days",
    title: "Average Time to Certification Readiness",
    desc: "Our structured approach and experienced audit teams deliver certification readiness in an average of 90 days. We streamline the process without cutting corners.",
  },
  {
    icon: Award,
    value: "IAF Aligned",
    title: "Internationally Accredited Certifications",
    desc: "All certifications are delivered through our network of internationally accredited partner bodies, ensuring global recognition and acceptance of your certificate.",
  },
  {
    icon: Zap,
    value: "24/7 SOC",
    title: "Continuous Security Monitoring",
    desc: "Beyond the audit, GACA offers continuous security monitoring and incident response services. We don't just certify you — we help keep you secure.",
  },
];

export default function WhyGacaPage() {
  const { t } = useLang();
  const wp = t?.why_page || {};
  const tAdvantages = wp.advantages || [];
  
  // Merge translated content with default features (icons and values)
  const features = defaultFeatures.map((def, i) => {
    const ta = tAdvantages[i];
    return {
      icon: def.icon,
      value: def.value,
      title: ta?.title || def.title,
      desc: ta?.desc || def.desc,
    };
  });
  return (
    <div className="page-transition pt-20" data-testid="why-gaca-page">
      <section className="bg-[#0B2545] py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#13A8BD] mb-4">{wp.badge || "Why Choose Us"}</p>
          <h1 data-testid="why-gaca-heading" className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black text-white leading-[1.1] mb-6" style={{ fontFamily: "Chivo, sans-serif" }}>
            {wp.title || "The GACA Advantage"}
          </h1>
          <p className="text-base md:text-lg text-white/65 max-w-2xl leading-relaxed">
            {wp.subtitle || "GACA International is not just another certification body. We are a certification board with cybersecurity at our core."}
          </p>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="space-y-0 border border-[#0B2545]/10">
            {features.map((f, i) => (
              <div key={f.value} data-testid={`advantage-${i}`} className={`grid grid-cols-1 lg:grid-cols-4 gap-0 border-b border-[#0B2545]/10 last:border-b-0 ${i % 2 === 1 ? 'bg-[#F7F9FC]' : 'bg-white'}`}>
                <div className="p-8 lg:p-12 flex flex-col items-center justify-center text-center border-r border-[#0B2545]/10">
                  <f.icon size={32} className="text-[#13A8BD] mb-3" strokeWidth={1.5} />
                  <div className="text-4xl font-black text-[#0B2545]" style={{ fontFamily: "Chivo, sans-serif" }}>{f.value}</div>
                </div>
                <div className="lg:col-span-3 p-8 lg:p-12">
                  <h2 className="text-xl font-bold text-[#0B2545] mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>{f.title}</h2>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#0B2545]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: "Chivo, sans-serif" }}>Start Your Certification Journey</h2>
          <p className="text-sm text-white/55 mb-6 max-w-md mx-auto">Apply for certification today and experience the GACA advantage.</p>
          <Link to="/contact" data-testid="why-gaca-cta" className="inline-flex items-center gap-2 bg-[#13A8BD] text-white px-8 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-[#0e8a9c] transition-colors">
            Apply for Certification <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
